/**
 * WorkforceMyPerformance — Aperture Bank Personal Banker "My Performance".
 *
 * REBUILT 2026-08-03 (Nick) against the operator's rulings. What it is now:
 * the staff-facing performance RECORD, computed from Marcus's own appointment
 * rows (serving engine → seed-world/<tenant>/performance.json), read in three
 * passes — his appointments first, then the branch, then his coaching history.
 *
 * The rulings this screen implements, so nobody has to re-derive them:
 *  · It IS the performance-management record, and it IS a comparison to other
 *    staff — but he never sees a peer. Only a branch AVERAGE (no rank, no
 *    distribution, no names): `peer` states the basis in words instead.
 *  · Every metric carries a GOAL with the manager who set it and the level it
 *    was set at (branch manager → branch, regional lead → region).
 *  · Appointments first: the period's real activity, the outcome mix, and a
 *    drillable list — every metric opens the appointments behind it.
 *  · No-shows AND cancellations count against him; the tile says so, and shows
 *    the split rather than hiding it behind one percentage.
 *  · Faster IS better on appointment time (operator ruling) — so the delta
 *    colours green when he is under branch/goal.
 *  · Customer score is a weighted blend of survey ratings and coaching
 *    assessments, weights set by the branch manager and approved regionally;
 *    the card shows the weighting and both components rather than one number.
 *  · Per-service comparison is branch-only, and honest about its 13-week
 *    ceiling (per-service branch facts stop where weekly roll-ups stop).
 *  · The branch reference is a REAL moving series, not today's average drawn
 *    flat backwards through history.
 *  · Coaching closes the loop: entries link to the metric they were about and
 *    whether it moved, he can acknowledge them and mark assigned training
 *    complete (both awaited ops), and manager-private entries never arrive
 *    here — the engine strips them before the response.
 *
 * Ops: getMyPerformance (extended, REQ-126) · updateMyCoachingEntry (new).
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Btn, DatePicker, Icon, Pill, T, alpha } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { EmptyState } from "../../shell/composites";
import { Drawer } from "../../shell/Overlay";
import { toast } from "../../shell/toast";
import { useWfResource, useBusy } from "./useWorkforce";
import {
  getMyPerformance, updateMyCoachingEntry,
  type MyPerformanceResponse, type MyPerfRange, type MyPerfAppointment, type MyCoachingEntry, type MyPerfGoal,
} from "./wf-api";
import { dayLabel, hmLabel } from "./wf-shared";
import { useT } from "../../lib/i18n";

const PRESETS: { id: MyPerfRange; l: string; k: string }[] = [
  { id: "7d", l: "Last Week", k: "workforce.myPerformance.presetLastWeek" },
  { id: "14d", l: "Last 2 Weeks", k: "workforce.myPerformance.presetLast2Weeks" },
  { id: "30d", l: "Last 30 Days", k: "workforce.myPerformance.presetLast30Days" },
  { id: "60d", l: "Last 60 Days", k: "workforce.myPerformance.presetLast60Days" },
  { id: "lastQuarter", l: "Last Quarter", k: "workforce.myPerformance.presetLastQuarter" },
  { id: "ytd", l: "This YTD", k: "workforce.myPerformance.presetThisYtd" },
  { id: "lastYear", l: "Last Year", k: "workforce.myPerformance.presetLastYear" },
];
const DEFAULT_PRESET: MyPerfRange = "14d";

type MetricId = "avgDurationMins" | "conversionPct" | "customerScore" | "noShowPct";
const METRICS: { id: MetricId; l: string; k: string; unit: string; digits: number; lowerBetter: boolean }[] = [
  { id: "avgDurationMins", l: "Avg appointment time", k: "workforce.myPerformance.avgAppointmentTime", unit: "m", digits: 0, lowerBetter: true },
  { id: "conversionPct", l: "Conversion", k: "workforce.myPerformance.conversion", unit: "%", digits: 0, lowerBetter: false },
  { id: "customerScore", l: "Customer score", k: "workforce.myPerformance.customerScore", unit: "/5", digits: 1, lowerBetter: false },
  { id: "noShowPct", l: "Missed appointments", k: "workforce.myPerformance.missedAppointments", unit: "%", digits: 0, lowerBetter: true },
];

const OUTCOMES: Record<string, { l: string; k: string; color: string }> = { // ui-config
  "sale-closed": { l: "Product opened", k: "workforce.myPerformance.outcomeSaleClosed", color: T.tGreen },
  "follow-up": { l: "Follow-up booked", k: "workforce.myPerformance.outcomeFollowUp", color: T.tDarkBlue },
  "in-progress": { l: "Still in progress", k: "workforce.myPerformance.outcomeInProgress", color: T.tLightBlue },
  "no-progress": { l: "No progress", k: "workforce.myPerformance.outcomeNoProgress", color: T.soft },
  escalation: { l: "Escalated", k: "workforce.myPerformance.outcomeEscalation", color: T.tOrange },
};
const KINDS: Record<string, { l: string; k: string; tone: "neutral" | "success" | "warn" | "danger" | "info" }> = { // ui-config
  "1on1": { l: "1:1", k: "workforce.myPerformance.kind1on1", tone: "info" },
  training: { l: "Training", k: "workforce.myPerformance.kindTraining", tone: "warn" },
  note: { l: "Note", k: "workforce.myPerformance.kindNote", tone: "neutral" },
  recognition: { l: "Recognition", k: "workforce.myPerformance.kindRecognition", tone: "success" },
};
const THEMES: Record<string, { l: string; k: string }> = { // ui-config
  "time-on-task": { l: "Time on task", k: "workforce.myPerformance.themeTimeOnTask" },
  conversion: { l: "Conversion", k: "workforce.myPerformance.themeConversion" },
  "customer-experience": { l: "Customer experience", k: "workforce.myPerformance.themeCustomerExperience" },
};

const card = { border: `1px solid ${T.border}`, borderRadius: 12, background: T.white, overflow: "hidden" as const, flexShrink: 0 };
/* By-service columns. Every track is SHRINKABLE with a floor, so the row fits its card at any
   width instead of pushing a fixed 506px budget past the container and scrolling sideways (the
   Service column was collapsing to ~41px at 613px of content). One constant, used by the header
   and the rows, so the two can never drift apart. */
const SVC_COLS = "minmax(132px, 1.5fr) repeat(5, minmax(62px, 1fr))";
const capStyle = { fontSize: 11, color: T.soft, textTransform: "uppercase" as const, letterSpacing: 0.05, fontWeight: 700 };
const mono = { fontFamily: T.fontMono };

/* Period controls. Two ways in, one window out:
 *  · a preset dropdown (the operator's list, in their order), and
 *  · an explicit start/end calendar.
 * Historical by contract — both pickers are capped at TODAY (`maxDate`), the end
 * date can never precede the start (`minDate` on End), and the start can never
 * follow the end. Picking a preset re-syncs the two dates to the window the
 * server actually served, so the calendar always shows what is on screen; moving
 * either date switches the label to "Custom range". No clear affordance: a
 * performance record always has a period. */
function PeriodControls({ preset, from, to, today, onPreset, onFrom, onTo }: {
  preset: MyPerfRange; from: string; to: string; today: string;
  onPreset: (r: MyPerfRange) => void; onFrom: (d: string) => void; onTo: (d: string) => void;
}) {
  const t = useT();
  const [open, setOpen] = useState(false); // ui-local
  const active = PRESETS.find((p) => p.id === preset);
  const label = active ? t(active.k, active.l) : t("workforce.myPerformance.customRange", "Custom range");
  const asDate = (iso: string) => { const [y, m, d] = iso.split("-").map(Number); return new Date(y, (m || 1) - 1, d || 1); };
  const dpValue = (iso: string) => `${iso}T00:00`;
  const dpPick = (v: string | null, apply: (d: string) => void) => { if (v && v.length >= 10) apply(v.slice(0, 10)); };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      <div style={{ position: "relative" }}>
        <button type="button" data-testid="wfmyperf-preset" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((o) => !o)}
          style={{ display: "inline-flex", alignItems: "center", gap: 8, height: 34, padding: "0 12px", borderRadius: 8, border: `1px solid ${open ? T.p1 : T.border}`, background: T.white, color: T.n1, fontSize: 13, fontWeight: 600, fontFamily: T.font, cursor: "pointer" }}>
          {label}
          <Icon.chev size={13} />
        </button>
        {open ? (
          <>
            <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
            <div role="listbox" style={{ position: "absolute", top: 40, left: 0, zIndex: 41, minWidth: 190, padding: 6, borderRadius: 10, border: `1px solid ${T.border}`, background: T.white, boxShadow: T.shadowLg }}>
              {PRESETS.map((p) => (
                <button key={p.id} type="button" role="option" aria-selected={p.id === preset} data-testid="wfmyperf-preset-option" data-id={p.id}
                  onClick={() => { onPreset(p.id); setOpen(false); }}
                  style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 10px", borderRadius: 6, border: "none", background: p.id === preset ? T.p3 : "transparent", color: p.id === preset ? T.p1 : T.n1, fontSize: 13, fontWeight: p.id === preset ? 700 : 500, fontFamily: T.font, textAlign: "left", cursor: "pointer" }}>
                  {t(p.k, p.l)}
                  {p.id === preset ? <Icon.check size={12} /> : null}
                </button>
              ))}
            </div>
          </>
        ) : null}
      </div>
      {/* ONE nowrap unit for the whole date range — label, both fields and the joining word.
          A bare label must never be a direct child of this wrapping row: as an independent
          child it becomes the wrap point's orphan (that is how "From" ended up sitting next
          to the preset dropdown with no field after it). Measured: 28 + 208 + 14 + 208 +
          3×8px gaps ≈ 482px, so the unit fits 613px of content and wraps whole below that. */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "nowrap" }}>
        <span style={{ fontSize: 11.5, color: T.soft, fontWeight: 600, flexShrink: 0 }}>{t("workforce.myPerformance.from", "From")}</span>
        <div style={{ width: 208 }}>
          <DatePicker size="sm" full showTime={false} allowPast includeDow={false} clearable={false} value={dpValue(from)}
            minDate={undefined} maxDate={asDate(to)} testid="wfmyperf-from"
            onChange={(v) => dpPick(v, onFrom)} />
        </div>
        <span style={{ fontSize: 11.5, color: T.soft, fontWeight: 600, flexShrink: 0 }}>{t("workforce.myPerformance.to", "to")}</span>
        <div style={{ width: 208 }}>
          <DatePicker size="sm" full showTime={false} allowPast includeDow={false} clearable={false} value={dpValue(to)}
            minDate={asDate(from)} maxDate={asDate(today)} testid="wfmyperf-to"
            onChange={(v) => dpPick(v, onTo)} />
        </div>
      </div>
    </div>
  );
}

function fmt(v: number | null | undefined, digits: number): string {
  if (v === null || v === undefined) return "—";
  return digits > 0 ? v.toFixed(digits) : String(Math.round(v));
}
function CardHead({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div style={{ padding: "15px 22px", borderBottom: `1px solid ${T.n6}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
      <span style={capStyle}>{children}</span>
      {right}
    </div>
  );
}

/* ── Block 1 · the period's real appointment activity ─────────────────── */
function ActivityStrip({ data, onOpen }: { data: MyPerformanceResponse; onOpen: (f: DrillFilter) => void }) {
  const t = useT();
  const a = data.activity;
  if (!a) return null;
  const ol = (id: string, fb: string) => { const meta = OUTCOMES[id]; return meta ? t(meta.k, meta.l) : fb; };
  /* Counts are window-true (they come from `totals`); per-day, hours, the outcome
     mix and the drill-down list can only come from appointment DETAIL, which the
     branch keeps for 30 days — so those are labelled rather than silently mixed. */
  const tot = data.totals;
  const held = tot ? tot.completed : a.completed;
  const missed = tot ? tot.noShows + tot.cancellations : a.noShows + a.cancellations;
  const plural = (n: number, oneK: string, oneL: string, manyK: string, manyL: string) => (n === 1 ? t(oneK, oneL) : t(manyK, manyL));
  const missedSub = tot
    ? `${tot.noShows} ${plural(tot.noShows, "workforce.myPerformance.noShow1", "no-show", "workforce.myPerformance.noShows", "no-shows")} · ${tot.cancellations} ${t("workforce.myPerformance.cancelled", "cancelled")}`
    : `${a.noShows} ${plural(a.noShows, "workforce.myPerformance.noShow1", "no-show", "workforce.myPerformance.noShows", "no-shows")} · ${a.cancellations} ${t("workforce.myPerformance.cancelled", "cancelled")}`;
  const detailScope = `${dayLabel(a.from, "md")} – ${dayLabel(a.to, "md")}`;
  /* A window that ends before the 30-day detail horizon (Last Quarter, Last Year,
     an older custom range) has roll-ups but no appointment rows. Say so — never
     print 0.0 per day as if he did nothing. */
  const hasDetail = a.hasDetail !== false && a.completed > 0;
  const dash = "—";
  const facts: { label: string; value: string; sub?: string; filter?: DrillFilter; tone?: string }[] = [
    { label: t("workforce.myPerformance.appointmentsHeld", "Appointments held"), value: String(held), sub: t("workforce.myPerformance.inThisPeriod", "in this period"), filter: { status: "completed" } },
    { label: t("workforce.myPerformance.perDay", "Per day"), value: hasDetail ? a.perDay.toFixed(1) : dash, sub: hasDetail ? `${a.days} ${plural(a.days, "workforce.myPerformance.dayOnTheDesk", "day on the desk", "workforce.myPerformance.daysOnTheDesk", "days on the desk")}` : undefined },
    { label: t("workforce.myPerformance.hoursWithCustomers", "Hours with customers"), value: hasDetail ? String(a.hours) : dash, sub: hasDetail ? `${a.minutes} ${t("workforce.myPerformance.minutesTotal", "minutes total")}` : undefined },
    { label: t("workforce.myPerformance.missed", "Missed"), value: String(missed), sub: missedSub, filter: { status: "missed" }, tone: missed > 0 ? T.orange1 : undefined },
    { label: t("workforce.myPerformance.flaggedForCoaching", "Flagged for coaching"), value: hasDetail ? String(a.flagged) : dash, sub: hasDetail ? t("workforce.myPerformance.byYouInMyDay", "by you, in My Day") : undefined, filter: hasDetail && a.flagged ? { flagged: true } : undefined, tone: hasDetail && a.flagged ? T.orange1 : undefined },
  ];
  return (
    <div style={{ ...card }}>
      <CardHead right={
        <button type="button" data-testid="wfmyperf-open-all-appointments" onClick={() => onOpen({})} disabled={!hasDetail}
          style={{ border: "none", background: "none", padding: 0, cursor: hasDetail ? "pointer" : "default", opacity: hasDetail ? 1 : 0.4, color: T.p1, fontSize: 12, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
          {t("workforce.myPerformance.viewAppointments", "View appointments")}
          <Icon.arrow size={13} />
        </button>
      }>
        {t("workforce.myPerformance.yourAppointments", "Your appointments")}
      </CardHead>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)" }}>
        {facts.map((f, i) => (
          <div key={f.label} data-testid="wfmyperf-activity-fact" data-id={f.label}
            onClick={f.filter ? () => onOpen(f.filter as DrillFilter) : undefined}
            style={{ padding: "20px 22px 22px", borderRight: i === facts.length - 1 ? "none" : `1px solid ${T.n6}`, display: "flex", flexDirection: "column", gap: 5, cursor: f.filter ? "pointer" : "default" }}>
            <span style={{ fontSize: 11.5, color: T.n2, fontWeight: 600 }}>{f.label}</span>
            <span style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.1, color: f.tone ?? T.n1, ...mono }}>{f.value}</span>
            {f.sub ? <span style={{ fontSize: 11, color: T.soft, lineHeight: 1.4 }}>{f.sub}</span> : null}
          </div>
        ))}
      </div>
      {a.partialWindow ? (
        <div style={{ padding: "11px 22px", borderTop: `1px solid ${T.n6}`, background: alpha(T.n1, 2), display: "flex", gap: 8, alignItems: "center" }}>
          <Icon.info size={13} />
          <span style={{ fontSize: 11.5, color: T.n2 }}>
            {hasDetail
              ? <>{t("workforce.myPerformance.perDayHoursTheOutcomeMixAndTheListCoverDetailFor", "Per day, hours, the outcome mix and the appointment list cover appointment-level detail for")} {detailScope} — {a.completed} {t("workforce.myPerformance.ofThem", "of them")}.</>
              : t("workforce.myPerformance.noDetailInThisWindow", "This period is older than the 30-day detail horizon, so per-day, hours, the outcome mix and the appointment list aren't available for it. Totals and comparisons come from roll-ups.")}
          </span>
        </div>
      ) : null}
      {data.outcomes && data.outcomes.length ? (
        <div style={{ padding: "18px 22px 20px", borderTop: `1px solid ${T.n6}`, display: "flex", flexDirection: "column", gap: 11 }}>
          <span style={{ fontSize: 11.5, color: T.n2, fontWeight: 600 }}>
            {t("workforce.myPerformance.howTheyEnded", "How they ended")}
            {a.partialWindow ? <span style={{ color: T.soft, fontWeight: 400 }}> · {t("workforce.myPerformance.fromTheLast30DaysOfDetail", "from the last 30 days of detail")}</span> : null}
          </span>
          <div style={{ display: "flex", height: 12, borderRadius: 6, overflow: "hidden", background: T.n6 }}>
            {data.outcomes.map((o) => (
              <div key={o.id} title={`${ol(o.id, o.label)} · ${o.count}`}
                onClick={() => onOpen({ outcome: o.id })}
                style={{ width: `${o.pct}%`, background: OUTCOMES[o.id]?.color ?? T.n2, cursor: "pointer" }} />
            ))}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px" }}>
            {data.outcomes.map((o) => (
              <button key={o.id} type="button" data-testid="wfmyperf-outcome-chip" data-id={o.id} onClick={() => onOpen({ outcome: o.id })}
                style={{ border: "none", background: "none", padding: 0, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: OUTCOMES[o.id]?.color ?? T.n2 }} />
                <span style={{ fontSize: 12, color: T.n1 }}>{ol(o.id, o.label)}</span>
                <span style={{ fontSize: 12, color: T.soft, ...mono }}>{o.count} · {o.pct}%</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ── Block 2 · a goal card per metric (goal + branch on one track) ────── */
function GoalCard({ metric, current, branch, goal, note, onOpen }: {
  metric: typeof METRICS[number]; current: number; branch: number;
  goal: MyPerfGoal | undefined;
  note?: ReactNode; onOpen: () => void;
}) {
  const t = useT();
  const target = goal ? goal.target : null;
  const better = (a: number, b: number) => (metric.lowerBetter ? a < b : a > b);
  const vsBranch = Math.round((current - branch) * 10) / 10;
  const branchTone = Math.abs(vsBranch) < (metric.digits ? 0.05 : 0.5) ? T.n2 : better(current, branch) ? T.green1 : T.red1;
  const metGoal = target === null ? null : better(current, target) || current === target;
  const domain = Math.max(current, branch, target ?? 0) * 1.3 || 1;
  const pos = (v: number) => `${Math.min(100, Math.max(0, (v / domain) * 100))}%`;
  return (
    <div data-testid="wfmyperf-goal-card" data-id={metric.id} onClick={onOpen}
      style={{ ...card, padding: "18px 20px 20px", display: "flex", flexDirection: "column", gap: 11, cursor: "pointer" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontSize: 11.5, color: T.n2, fontWeight: 600 }}>{t(metric.k, metric.l)}</span>
        {metGoal === null ? null : (
          <Pill tone={metGoal ? "success" : "warn"}>{metGoal ? t("workforce.myPerformance.onGoal", "On goal") : t("workforce.myPerformance.offGoal", "Off goal")}</Pill>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 9, flexWrap: "wrap" }}>
        <span style={{ fontSize: 29, fontWeight: 700, lineHeight: 1.1, color: T.n1, ...mono }}>{fmt(current, metric.digits)}{metric.unit}</span>
        <span style={{ fontSize: 11.5, fontWeight: 600, color: branchTone, ...mono }}>
          {vsBranch > 0 ? "+" : ""}{fmt(vsBranch, metric.digits)}{metric.unit} {t("workforce.myPerformance.vsBranch", "vs branch")}
        </span>
      </div>
      <div style={{ position: "relative", height: 6, borderRadius: 3, background: T.n6, margin: "4px 0 2px" }}>
        <div style={{ position: "absolute", inset: 0, width: pos(current), background: metGoal === false ? T.orange1 : T.p1, borderRadius: 3 }} />
        <span title={t("workforce.myPerformance.branchAverage", "Branch average")} style={{ position: "absolute", left: pos(branch), top: -3, width: 2, height: 12, background: T.n1, opacity: 0.55 }} />
        {target !== null ? <span title={t("workforce.myPerformance.goal", "Goal")} style={{ position: "absolute", left: pos(target), top: -5, width: 2, height: 16, background: T.n1 }} /> : null}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "3px 12px", fontSize: 11, color: T.soft }}>
        {target !== null ? <span style={mono}>{t("workforce.myPerformance.goal", "Goal")} {fmt(target, metric.digits)}{metric.unit}</span> : null}
        <span style={mono}>{t("workforce.myPerformance.branch", "Branch")} {fmt(branch, metric.digits)}{metric.unit}</span>
      </div>
      {goal ? (
        <span style={{ fontSize: 11, color: T.soft, lineHeight: 1.45 }}>
          {t("workforce.myPerformance.setBy", "Set by")} {goal.setBy} · {goal.setByRole}
        </span>
      ) : (
        <span style={{ fontSize: 11, color: T.soft }}>{t("workforce.myPerformance.noGoalSetForThisMetric", "No goal set for this metric")}</span>
      )}
      {note}
    </div>
  );
}

/* ── Block 3 · me vs a MOVING branch series, with the goal line ───────── */
type ChartMetric = "duration" | "conversion" | "survey";
function TrendChart({ data, metric }: { data: MyPerformanceResponse; metric: ChartMetric }) {
  const t = useT();
  const pts = data.trend;
  const goalFor: Record<ChartMetric, MetricId> = { duration: "avgDurationMins", conversion: "conversionPct", survey: "customerScore" };
  const goal = (data.goals ?? []).find((g) => g.metric === goalFor[metric]);
  const showGoal = metric !== "survey" && goal ? goal.target : null;
  const mine = pts.map((p) => (metric === "duration" ? p.avgDurationMins : metric === "conversion" ? p.conversionPct : (p.surveyScore ?? null)));
  const brs = pts.map((p) => (metric === "duration" ? (p.branchAvgDurationMins ?? null) : metric === "conversion" ? (p.branchConversionPct ?? null) : (p.branchSurveyScore ?? null)));
  if (pts.length < 2) return <span style={{ fontSize: 12, color: T.soft }}>{t("workforce.myPerformance.notEnoughDataYet", "Not enough data yet.")}</span>;

  const nums = ([] as number[]).concat(mine.filter((v): v is number => v !== null), brs.filter((v): v is number => v !== null), showGoal !== null ? [showGoal] : []);
  if (nums.length === 0) return <span style={{ fontSize: 12, color: T.soft }}>{t("workforce.myPerformance.notEnoughDataYet", "Not enough data yet.")}</span>;
  const hi = Math.max(...nums), lo = Math.min(...nums);
  const max = hi + (hi - lo || hi * 0.2) * 0.35, min = Math.max(0, lo - (hi - lo || hi * 0.2) * 0.35);
  const W = 900, H = 190, PAD = { l: 34, r: 12, t: 12, b: 30 };
  const iw = W - PAD.l - PAD.r, ih = H - PAD.t - PAD.b;
  const px = (i: number) => PAD.l + (i / (pts.length - 1)) * iw;
  const py = (v: number) => PAD.t + (1 - (v - min) / (max - min || 1)) * ih;
  const line = (vals: (number | null)[]) => vals.map((v, i) => (v === null ? null : `${i === 0 || vals[i - 1] === null ? "M" : "L"}${px(i)},${py(v)}`)).filter(Boolean).join(" ");
  const digits = metric === "survey" ? 1 : 0;
  const unit = metric === "duration" ? "m" : metric === "conversion" ? "%" : "";
  const label = (i: number) => {
    const p = pts[i];
    if (p.period === "month") return dayLabel(p.date, "md").replace(/\s?\d+$/, "");
    return dayLabel(p.date, "md");
  };
  const step = pts.length > 8 ? 2 : 1;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" style={{ width: "100%", height: 200, display: "block" }}
      aria-label={`${t("workforce.myPerformance.yourFigureVsTheBranchAverageOverTime", "Your figure vs the branch average over time")} — ${fmt(mine[mine.length - 1], digits)}${unit}`}>
      {[min, (min + max) / 2, max].map((v, i) => (
        <g key={i}>
          <line x1={PAD.l} y1={py(v)} x2={W - PAD.r} y2={py(v)} stroke={T.n6} strokeWidth={1} />
          <text x={PAD.l - 6} y={py(v) + 3.5} textAnchor="end" fontSize="10" fill={T.soft} fontFamily={T.fontMono}>{fmt(v, digits)}</text>
        </g>
      ))}
      {showGoal !== null ? (
        <g>
          <line x1={PAD.l} y1={py(showGoal)} x2={W - PAD.r} y2={py(showGoal)} stroke={T.n1} strokeWidth={1.2} strokeDasharray="5 4" />
          <text x={W - PAD.r} y={py(showGoal) - 5} textAnchor="end" fontSize="10.5" fontWeight={700} fill={T.n1} fontFamily={T.fontMono}>
            {t("workforce.myPerformance.goal", "Goal")} {fmt(showGoal, digits)}{unit}
          </text>
        </g>
      ) : null}
      <path d={line(brs)} fill="none" stroke={T.soft} strokeWidth={2} strokeDasharray="4 3" strokeLinecap="round" />
      <path d={line(mine)} fill="none" stroke={T.p1} strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" />
      {mine.map((v, i) => v === null ? null : (
        <g key={i}>
          <circle cx={px(i)} cy={py(v)} r={i === pts.length - 1 ? 4.5 : 3} fill={pts[i].partial ? T.white : T.p1} stroke={T.p1} strokeWidth={2} />
          {i === pts.length - 1 || i === 0 ? (
            <text x={px(i)} y={py(v) - 10} textAnchor={i === 0 ? "start" : "end"} fontSize="10.5" fontWeight={700} fill={T.n1} fontFamily={T.fontMono}>{fmt(v, digits)}{unit}</text>
          ) : null}
        </g>
      ))}
      {pts.map((p, i) => (i % step === 0 || i === pts.length - 1) ? (
        <text key={i} x={px(i)} y={H - 10} textAnchor="middle" fontSize="10" fill={T.soft} fontFamily={T.fontMono}>{label(i)}</text>
      ) : null)}
    </svg>
  );
}

/* ── Block 5 · one coaching entry, with the two real actions ──────────── */
function CoachingRow({ entry, busy, onAck, onTraining, onReflect, last }: {
  entry: MyCoachingEntry; busy: string | null; last: boolean;
  onAck: (e: MyCoachingEntry) => void; onTraining: (e: MyCoachingEntry) => void; onReflect: (e: MyCoachingEntry, text: string) => void;
}) {
  const t = useT();
  const [draft, setDraft] = useState(entry.reflection ?? ""); // ui-local (buffer; persisted on save)
  const kind = KINDS[entry.kind ?? "note"] ?? KINDS.note;
  const theme = entry.theme ? THEMES[entry.theme] : null;
  const metric = METRICS.find((m) => m.id === entry.metric);
  const overdue = !!entry.dueDate && !entry.completedAt && entry.dueDate < new Date().toISOString().slice(0, 10);
  const dirTone = entry.direction === "improving" ? T.green1 : entry.direction === "slipping" ? T.red1 : T.n2;
  return (
    <div data-testid="wfmyperf-coaching-row" data-id={entry.id}
      style={{ padding: "20px 22px", borderBottom: last ? "none" : `1px solid ${T.n6}`, display: "flex", flexDirection: "column", gap: 11 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Pill tone={kind.tone}>{t(kind.k, kind.l)}</Pill>
          <span style={{ fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{entry.title}</span>
          {theme ? <span style={{ fontSize: 11, color: T.soft }}>{t(theme.k, theme.l)}</span> : null}
        </div>
        <span style={{ fontSize: 11, color: T.soft, flexShrink: 0, ...mono }}>{dayLabel(entry.date, "md")}</span>
      </div>
      <p style={{ fontSize: 12.5, color: T.n2, lineHeight: 1.55, margin: 0 }}>{entry.body}</p>
      {metric && entry.baseline !== null && entry.baseline !== undefined ? (
        <div style={{ display: "inline-flex", alignSelf: "flex-start", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: 8, background: T.n6 }}>
          <span style={{ fontSize: 11, color: T.n2, fontWeight: 600 }}>{t(metric.k, metric.l)}</span>
          <span style={{ fontSize: 11.5, color: T.soft, ...mono }}>{fmt(entry.baseline, metric.digits)}{metric.unit}</span>
          <Icon.arrow size={11} />
          <span style={{ fontSize: 11.5, fontWeight: 700, color: dirTone, ...mono }}>{fmt(entry.latest ?? null, metric.digits)}{metric.unit}</span>
          <span style={{ fontSize: 11, color: dirTone, fontWeight: 600 }}>
            {entry.direction === "improving" ? t("workforce.myPerformance.movedTheRightWay", "moved the right way")
              : entry.direction === "slipping" ? t("workforce.myPerformance.movedTheWrongWay", "moved the wrong way")
              : t("workforce.myPerformance.unchanged", "unchanged")}
          </span>
        </div>
      ) : null}
      {entry.training ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "9px 12px", borderRadius: 8, border: `1px solid ${overdue ? T.red1 : T.border}`, background: overdue ? alpha(T.red1, 6) : T.white }}>
          <Icon.tags size={13} />
          <span style={{ fontSize: 12, color: T.n1, fontWeight: 600 }}>{entry.training.provider} · {entry.training.minutes} {t("workforce.myPerformance.min", "min")}</span>
          {entry.dueDate ? (
            <span style={{ fontSize: 11.5, color: overdue ? T.red1 : T.soft, fontWeight: overdue ? 700 : 400, ...mono }}>
              {entry.completedAt ? t("workforce.myPerformance.wasDue", "was due") : overdue ? t("workforce.myPerformance.overdue", "overdue —") : t("workforce.myPerformance.due", "due")} {dayLabel(entry.dueDate, "md")}
            </span>
          ) : null}
          <div style={{ marginLeft: "auto" }}>
            {entry.completedAt
              ? <Pill tone="success" dot>{t("workforce.myPerformance.completed", "Completed")}</Pill>
              : <Btn size="sm" variant="secondary" disabled={busy === `tr:${entry.id}`} onClick={() => onTraining(entry)} testid="wfmyperf-training-complete">
                  {busy === `tr:${entry.id}` ? t("workforce.myPerformance.saving", "Saving…") : t("workforce.myPerformance.markComplete", "Mark complete")}
                </Btn>}
          </div>
        </div>
      ) : null}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11.5, color: T.soft }}>— {entry.by}{entry.byRole ? `, ${entry.byRole}` : ""}</span>
        {entry.assessment !== null && entry.assessment !== undefined ? (
          <span style={{ fontSize: 11.5, color: T.soft, ...mono }}>{t("workforce.myPerformance.assessment", "Assessment")} {entry.assessment.toFixed(1)}/5</span>
        ) : null}
        <div style={{ marginLeft: "auto" }}>
          {entry.acknowledgedAt
            ? <span style={{ fontSize: 11.5, color: T.green1, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 5 }}>
                <Icon.check size={12} />{t("workforce.myPerformance.acknowledged", "Acknowledged")} {dayLabel(entry.acknowledgedAt.slice(0, 10), "md")}
              </span>
            : <Btn size="sm" disabled={busy === `ack:${entry.id}`} onClick={() => onAck(entry)} testid="wfmyperf-acknowledge">
                {busy === `ack:${entry.id}` ? t("workforce.myPerformance.saving", "Saving…") : t("workforce.myPerformance.acknowledge", "Acknowledge")}
              </Btn>}
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
        <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2}
          data-testid="wfmyperf-reflection" placeholder={t("workforce.myPerformance.addYourOwnNoteYourManagerCanSeeIt", "Add your own note — your manager can see it")}
          style={{ flex: 1, resize: "vertical", padding: "8px 10px", borderRadius: 8, border: `1px solid ${T.border}`, fontSize: 12.5, fontFamily: T.font, color: T.n1, lineHeight: 1.5 }} />
        <Btn size="sm" variant="secondary" disabled={busy === `rf:${entry.id}` || draft.trim() === (entry.reflection ?? "").trim()}
          onClick={() => onReflect(entry, draft.trim())} testid="wfmyperf-reflection-save">
          {busy === `rf:${entry.id}` ? t("workforce.myPerformance.saving", "Saving…") : t("workforce.myPerformance.save", "Save")}
        </Btn>
      </div>
    </div>
  );
}

/* ── The drill-down: every metric, outcome and service opens this ─────── */
interface DrillFilter { service?: string; outcome?: string; status?: "completed" | "missed"; flagged?: boolean; sort?: MetricId }
function AppointmentsDrawer({ data, filter, onClose }: { data: MyPerformanceResponse; filter: DrillFilter; onClose: () => void }) {
  const t = useT();
  const ol = (id: string, fb: string) => { const meta = OUTCOMES[id]; return meta ? t(meta.k, meta.l) : fb; };
  const rows = useMemo(() => {
    let out = (data.appointments ?? []).slice();
    if (filter.service) out = out.filter((a) => a.service === filter.service);
    if (filter.outcome) out = out.filter((a) => a.outcome === filter.outcome);
    if (filter.status === "completed") out = out.filter((a) => a.status === "completed");
    if (filter.status === "missed") out = out.filter((a) => a.status !== "completed");
    if (filter.flagged) out = out.filter((a) => a.coachingFlag);
    if (filter.sort === "avgDurationMins") out.sort((a, b) => b.durationMin - a.durationMin);
    if (filter.sort === "customerScore") out.sort((a, b) => (a.csat ?? 9) - (b.csat ?? 9));
    return out;
  }, [data.appointments, filter]);
  const sub = [
    filter.service, filter.outcome ? ol(filter.outcome, filter.outcome) : null,
    filter.status === "missed" ? t("workforce.myPerformance.noShowsAndCancellations", "no-shows and cancellations") : null,
    filter.flagged ? t("workforce.myPerformance.flaggedForCoaching", "Flagged for coaching") : null,
    filter.sort === "avgDurationMins" ? t("workforce.myPerformance.longestFirst", "longest first") : null,
    filter.sort === "customerScore" ? t("workforce.myPerformance.lowestRatedFirst", "lowest rated first") : null,
  ].filter(Boolean).join(" · ");
  const rangeLabel = data.activity ? `${dayLabel(data.activity.from, "md")} – ${dayLabel(data.activity.to, "md")}` : "";
  const partial = !!data.activity?.partialWindow;
  return (
    <Drawer panel="myperf-appointments" width={620} testid="wfmyperf-appointments-drawer" onClose={onClose}
      title={`${rows.length} ${t("workforce.myPerformance.appointments", "appointments")}`}
      sub={[sub, rangeLabel].filter(Boolean).join(" · ")}>
      {partial ? (
        <div style={{ padding: "12px 22px", background: alpha(T.n1, 2), borderBottom: `1px solid ${T.n6}`, fontSize: 11.5, color: T.n2, display: "flex", gap: 8, alignItems: "center" }}>
          <Icon.info size={13} />
          <span>{t("workforce.myPerformance.detailIsKeptFor30Days", "Appointment-level detail is kept for 30 days")}{data.totals ? ` — ${data.totals.completed} ${t("workforce.myPerformance.heldInTheSelectedPeriod", "held in the selected period")}` : ""}.</span>
        </div>
      ) : null}
      {rows.length === 0 ? (
        <div style={{ padding: 12 }}><EmptyState icon={<span />} title={t("workforce.myPerformance.nothingMatchesThatFilter", "Nothing matches that filter")} body={t("workforce.myPerformance.appointmentLevelDetailCoversTheLast30Days", "Appointment-level detail covers the last 30 days.")} /></div>
      ) : rows.map((a: MyPerfAppointment) => (
        <div key={a.id} data-testid="wfmyperf-appointment-row" data-id={a.id}
          style={{ display: "grid", gridTemplateColumns: "92px 1fr 66px 100px", gap: 12, alignItems: "center", padding: "14px 22px", borderBottom: `1px solid ${T.n6}` }}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontSize: 12, color: T.n1, fontWeight: 600, ...mono }}>{dayLabel(a.date, "md")}</span>
            <span style={{ fontSize: 11, color: T.soft, ...mono }}>{hmLabel(a.time)}</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <span style={{ fontSize: 13, color: T.n1, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.customer}</span>
            <span style={{ fontSize: 11.5, color: T.soft, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {a.service}{a.coachingFlag ? ` · ${t("workforce.myPerformance.flagged", "flagged")}` : ""}
            </span>
          </div>
          <span style={{ fontSize: 12, color: T.n2, textAlign: "right", ...mono }}>{t("common.units.minutesCompact", "{n}m").replace("{n}", String(a.durationMin))}</span>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3 }}>
            {a.status !== "completed"
              ? <Pill tone="danger">{a.status === "no-show" ? t("workforce.myPerformance.noShow", "No-show") : t("workforce.myPerformance.cancelledPill", "Cancelled")}</Pill>
              : a.outcome
                ? <span style={{ fontSize: 11, fontWeight: 700, color: OUTCOMES[a.outcome]?.color ?? T.n2, textAlign: "right" }}>{ol(a.outcome, a.outcome)}</span>
                : null}
            {typeof a.csat === "number" ? <span style={{ fontSize: 11, color: T.soft, ...mono }}>{a.csat}/5</span> : null}
          </div>
        </div>
      ))}
    </Drawer>
  );
}

const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const shiftDays = (n: number) => { const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + n); return isoDay(d); };

export default function WorkforceMyPerformance() {
  const t = useT();
  const today = isoDay(new Date());
  const [preset, setPreset] = useState<MyPerfRange>(DEFAULT_PRESET); // ui-local
  /* Historical by default and by construction: end = today, start = two weeks back. */
  const [from, setFrom] = useState<string>(shiftDays(-14)); // ui-local
  const [to, setTo] = useState<string>(today); // ui-local
  const [chart, setChart] = useState<ChartMetric>("duration"); // ui-local
  const [drill, setDrill] = useState<DrillFilter | null>(null); // ui-local
  const custom = preset === "custom";
  const { data, loading, error, reload } = useWfResource<MyPerformanceResponse>(
    `myperf:${preset}:${custom ? `${from}:${to}` : ""}`,
    () => getMyPerformance(preset, custom ? { from, to } : undefined),
  );
  /* A preset owns the dates: re-sync the calendar to the window the server
     actually served, so the two controls never disagree. */
  useEffect(() => {
    if (custom || !data?.coverage) return;
    setFrom(data.coverage.from);
    setTo(data.coverage.to);
  }, [custom, data?.coverage?.from, data?.coverage?.to]);
  const pickFrom = (d: string) => { setPreset("custom"); setFrom(d); if (d > to) setTo(d > today ? today : d); };
  const pickTo = (d: string) => { const capped = d > today ? today : d; setPreset("custom"); setTo(capped); if (from > capped) setFrom(capped); };
  const { busy, run } = useBusy();
  const m = data?.metrics;
  const goalOf = (id: MetricId) => (data?.goals ?? []).find((g) => g.metric === id);

  const patch = (id: string, key: string, input: { acknowledged?: boolean; trainingCompleted?: boolean; reflection?: string }, ok: string) =>
    run(key, async () => {
      try {
        await updateMyCoachingEntry(id, input);
        toast({ tone: "success", title: ok });
        reload();
      } catch (e) {
        toast({ tone: "danger", title: t("workforce.myPerformance.couldnTSaveThat", "Couldn't save that — try again.") });
        console.warn("updateMyCoachingEntry failed", e);
      }
    });

  const CHART_TABS: { id: ChartMetric; l: string; k: string }[] = [
    { id: "duration", l: "Avg time", k: "workforce.myPerformance.avgTime" },
    { id: "conversion", l: "Conversion", k: "workforce.myPerformance.conversion" },
    { id: "survey", l: "Survey score", k: "workforce.myPerformance.surveyScore" },
  ];

  return (
    <Screen rail="wf-myperf">
      <PageHeader title={t("workforce.myPerformance.myPerformance", "My Performance")}
        /* No subtitle (operator, 2026-08-03): the branch and role were noise — he knows where
           he works, and the period caption below carries the branch context that matters
           ("branch is averaged across 5 personal bankers · Boston Back Bay"). */
        /* `false`, not `null`: PageHeader's guard is
           `breadcrumb !== null && breadcrumb !== undefined ? breadcrumb : <Dashboard link>`,
           so passing null FALLS THROUGH to the default "← Dashboard" (chrome.tsx:62). Marcus
           has no Dashboard in his rail and it isn't scoped to him — his landing route is
           My Day. Suppressed here with a falsy non-null value until the shared guard is
           fixed (~35 screens pass null with the same intent). */
        breadcrumb={false} />
      <DataSourceStrip screen="wfmyperf" ops={["getMyPerformance", "updateMyCoachingEntry"]} reqId="REQ-126" />
      <div style={{ flex: 1, overflow: "auto", padding: "18px 24px 40px", display: "flex", flexDirection: "column", gap: 22 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 9, flexShrink: 0 }}>
          <PeriodControls preset={preset} from={from} to={to} today={today}
            onPreset={setPreset} onFrom={pickFrom} onTo={pickTo} />
          {data?.coverage ? (
            <span style={{ fontSize: 11.5, color: T.soft }}>
              {dayLabel(data.coverage.from, "md")} – {dayLabel(data.coverage.to, "md")} · {data.coverage.days} {t("workforce.myPerformance.days", "days")}
              {data.peer ? ` · ${t("workforce.myPerformance.branchIsAveragedAcross", "branch is averaged across")} ${data.peer.count + 1} ${data.peer.role.toLowerCase()}s${data.locationName ? ` · ${data.locationName}` : ""}` : ""}
            </span>
          ) : null}
        </div>

        {error ? (
          <div style={{ padding: 40, textAlign: "center", color: T.soft }}>{t("workforce.myPerformance.couldnTLoadYourPerformance", "Couldn't load your performance:")}{" "}{error}</div>
        ) : loading || !m || !data ? (
          <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t("workforce.myPerformance.loadingYourPerformance", "Loading your performance…")}</div>
        ) : (data.totals?.booked ?? 0) === 0 ? (
          <div style={{ ...card, padding: 12 }}>
            <EmptyState icon={<span />} title={t("workforce.myPerformance.noAppointmentsInThisPeriod", "No appointments in this period")}
              body={t("workforce.myPerformance.pickAWiderRangeOrCheckBackAfterYourNextShift", "Pick a wider range, or check back after your next shift.")} />
          </div>
        ) : (
          <>
            <ActivityStrip data={data} onOpen={setDrill} />

            {data.metricsAvailable === false ? (
              <div style={{ ...card, padding: "18px 22px", display: "flex", gap: 10, alignItems: "flex-start" }}>
                <Icon.info size={15} />
                <span style={{ fontSize: 12.5, color: T.n2, lineHeight: 1.55 }}>
                  {t("workforce.myPerformance.tooFewAppointmentsToCompareYet", "Too few appointments in this period to compare against branch or goal — the figures below would be noise. Pick a wider range.")}
                </span>
              </div>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, flexShrink: 0 }}>
                  <GoalCard metric={METRICS[0]} current={m.avgDurationMins} branch={m.branchAvgDurationMins} goal={goalOf("avgDurationMins")} onOpen={() => setDrill({ sort: "avgDurationMins" })} />
                  <GoalCard metric={METRICS[1]} current={m.conversionPct} branch={m.branchConversionPct} goal={goalOf("conversionPct")} onOpen={() => setDrill({ outcome: "sale-closed" })} />
                  <GoalCard metric={METRICS[2]} current={m.customerScore} branch={m.branchCustomerScore} goal={goalOf("customerScore")}
                    onOpen={() => setDrill({ sort: "customerScore" })}
                    note={data.customerScoreModel ? (
                      <span style={{ fontSize: 11, color: T.soft, lineHeight: 1.45 }}>
                        {data.customerScoreModel.surveyWeightPct}% {t("workforce.myPerformance.surveys", "surveys")} ({fmt(data.customerScoreModel.surveyScore, 1)}, {data.customerScoreModel.surveyResponses} {t("workforce.myPerformance.responses", "responses")})
                        {" · "}{data.customerScoreModel.coachingWeightPct}% {t("workforce.myPerformance.coaching", "coaching")} ({fmt(data.customerScoreModel.coachingScore, 1)})
                      </span>
                    ) : undefined} />
                  <GoalCard metric={METRICS[3]} current={m.noShowPct} branch={m.branchNoShowPct} goal={goalOf("noShowPct")}
                    onOpen={() => setDrill({ status: "missed" })}
                    note={<span style={{ fontSize: 11, color: T.soft, lineHeight: 1.45 }}>{t("workforce.myPerformance.noShowsAndCancellationsBothCount", "No-shows and cancellations both count")}</span>} />
                </div>

                <div style={card}>
                  <CardHead right={
                    <div style={{ display: "flex", gap: 4 }}>
                      {CHART_TABS.map((tab) => (
                        <button key={tab.id} type="button" data-testid="wfmyperf-chart-tab" data-id={tab.id} onClick={() => setChart(tab.id)}
                          style={{ padding: "4px 10px", borderRadius: 6, border: `1px solid ${chart === tab.id ? T.p1 : T.border}`, background: chart === tab.id ? alpha(T.p1, 8) : T.white, color: chart === tab.id ? T.p1 : T.n2, fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: T.font }}>
                          {t(tab.k, tab.l)}
                        </button>
                      ))}
                    </div>
                  }>
                    {data.coverage?.granularity === "month"
                      ? t("workforce.myPerformance.monthByMonthVsBranch", "Month by month, vs branch")
                      : t("workforce.myPerformance.weekByWeekVsBranch", "Week by week, vs branch")}
                  </CardHead>
                  <div style={{ padding: "16px 22px 6px" }}><TrendChart data={data} metric={chart} /></div>
                  <div style={{ padding: "0 22px 18px", display: "flex", gap: 18, flexWrap: "wrap", alignItems: "center" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: T.n2 }}>
                      <span style={{ width: 16, height: 2.5, background: T.p1, borderRadius: 2 }} />{t("workforce.myPerformance.you", "You")}
                    </span>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: T.n2 }}>
                      <span style={{ width: 16, height: 2, background: T.soft, borderRadius: 2 }} />{t("workforce.myPerformance.branchAverage", "Branch average")}
                    </span>
                    {data.trend.some((p) => p.partial) ? (
                      <span style={{ fontSize: 11, color: T.soft }}>{t("workforce.myPerformance.hollowPointIsTheCurrentIncompletePeriod", "Hollow point is the current, incomplete period")}</span>
                    ) : null}
                  </div>
                </div>

                <div style={card}>
                  <CardHead right={<span style={{ fontSize: 11, color: T.soft, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>
                    {t("workforce.myPerformance.branchFiguresCoverTheLast13Weeks", "Branch figures cover the last 13 weeks")}
                  </span>}>
                    {t("workforce.myPerformance.byServiceVsBranch", "By service, vs branch")}
                  </CardHead>
                  <div style={{ display: "grid", gridTemplateColumns: SVC_COLS, gap: 10, padding: "11px 22px", borderBottom: `1px solid ${T.n6}`, background: alpha(T.n1, 2) }}>
                    {[
                      { l: "Service", k: "workforce.myPerformance.service", a: "left" },
                      { l: "Held", k: "workforce.myPerformance.held", a: "right" },
                      { l: "Share of book", k: "workforce.myPerformance.shareOfBook", a: "right" },
                      { l: "Avg time", k: "workforce.myPerformance.avgTime", a: "right" },
                      { l: "Conversion", k: "workforce.myPerformance.conversion", a: "right" },
                      { l: "Survey", k: "workforce.myPerformance.survey", a: "right" },
                    ].map((h) => (
                      <span key={h.k} style={{ ...capStyle, fontSize: 10, textAlign: h.a as "left" | "right" }}>{t(h.k, h.l)}</span>
                    ))}
                  </div>
                  {data.byService.length === 0 ? (
                    <div style={{ padding: 12 }}>
                      <EmptyState icon={<span />} title={t("workforce.myPerformance.noPerServiceBreakdownForThisPeriod", "No per-service breakdown for this period")}
                        body={t("workforce.myPerformance.perServiceFiguresAreKeptFor13Weeks", "Per-service figures are kept for 13 weeks. Older periods keep their totals and comparisons, but not the service split.")} />
                    </div>
                  ) : data.byService.map((s, i) => {
                    const durDelta = s.branchAvgDurationMins === null || s.branchAvgDurationMins === undefined ? null : s.avgDurationMins - s.branchAvgDurationMins;
                    const convDelta = s.branchConversionPct === null || s.branchConversionPct === undefined ? null : s.conversionPct - s.branchConversionPct;
                    const shareDelta = s.branchSharePct === null || s.branchSharePct === undefined || s.sharePct === undefined ? null : s.sharePct - s.branchSharePct;
                    const tone = (d: number | null, lower: boolean) => d === null || Math.abs(d) < 1 ? T.soft : (lower ? d < 0 : d > 0) ? T.green1 : T.red1;
                    return (
                      <div key={s.service} data-testid="wfmyperf-service-row" data-id={s.service} onClick={() => setDrill({ service: s.service })}
                        style={{ display: "grid", gridTemplateColumns: SVC_COLS, gap: 10, padding: "15px 22px", borderBottom: i === data.byService.length - 1 ? "none" : `1px solid ${T.n6}`, alignItems: "center", cursor: "pointer" }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.service}</span>
                        <span style={{ fontSize: 12.5, color: T.n1, textAlign: "right", ...mono }}>{s.count}</span>
                        <span style={{ fontSize: 12, textAlign: "right", ...mono, color: T.n2 }}>
                          {s.sharePct ?? 0}%
                          {shareDelta === null ? null : <span style={{ color: Math.abs(shareDelta) < 4 ? T.soft : T.orange1, fontWeight: 600 }}> ({shareDelta > 0 ? "+" : ""}{shareDelta})</span>}
                        </span>
                        <span style={{ fontSize: 12, textAlign: "right", ...mono, color: T.n2 }}>
                          {t("common.units.minutesCompact", "{n}m").replace("{n}", String(s.avgDurationMins))}
                          {durDelta === null ? null : <span style={{ color: tone(durDelta, true), fontWeight: 700 }}> {durDelta > 0 ? "+" : ""}{durDelta}</span>}
                        </span>
                        <span style={{ fontSize: 12, textAlign: "right", ...mono, color: T.n2 }}>
                          {s.conversionPct}%
                          {convDelta === null ? null : <span style={{ color: tone(convDelta, false), fontWeight: 700 }}> {convDelta > 0 ? "+" : ""}{convDelta}</span>}
                        </span>
                        <span style={{ fontSize: 12, textAlign: "right", ...mono, color: T.n2 }}>{s.csat === null || s.csat === undefined ? "—" : s.csat.toFixed(1)}</span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            <div style={card}>
              <CardHead right={data.customerScoreModel?.setBy ? (
                <span style={{ fontSize: 11, color: T.soft, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>
                  {t("workforce.myPerformance.anythingYouWriteHereIsVisibleToYourManager", "Anything you write here is visible to your manager")}
                </span>
              ) : undefined}>
                {t("workforce.myPerformance.coachingHistory", "Coaching history")}
              </CardHead>
              {data.coachingLog.length === 0 ? (
                <div style={{ padding: 12 }}>
                  <EmptyState icon={<span />} title={t("workforce.myPerformance.noCoachingEntriesYet", "No coaching entries yet")}
                    body={t("workforce.myPerformance.notesAndPlansFromYourManagerWillShowUpHere", "Notes and plans from your manager will show up here.")} />
                </div>
              ) : data.coachingLog.map((c, i) => (
                <CoachingRow key={c.id} entry={c} busy={busy} last={i === data.coachingLog.length - 1}
                  onAck={(e) => patch(e.id, `ack:${e.id}`, { acknowledged: true }, t("workforce.myPerformance.acknowledgedToast", "Acknowledged — your manager can see that you've read it."))}
                  onTraining={(e) => patch(e.id, `tr:${e.id}`, { trainingCompleted: true }, t("workforce.myPerformance.trainingMarkedComplete", "Training marked complete."))}
                  onReflect={(e, text) => patch(e.id, `rf:${e.id}`, { reflection: text }, t("workforce.myPerformance.noteSaved", "Note saved."))} />
              ))}
            </div>
          </>
        )}
      </div>
      {drill && data ? <AppointmentsDrawer data={data} filter={drill} onClose={() => setDrill(null)} /> : null}
    </Screen>
  );
}
